import { memo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ComponentMeta } from "../BuilderUI.types";
import { IFRAME_ID } from "../Frames/constants";
import { getBlockName } from "../Helpers/getBlockName";

export interface PreviewBlockHoverProps {
  blockMeta: ComponentMeta;
  hoveredElement: HTMLElement;
}

export const PreviewBlockHover = memo(
  ({ blockMeta, hoveredElement }: PreviewBlockHoverProps) => {
    const [position, setPosition] = useState(() => {
      const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;
      if (!iframe) return null;

      const hoveredRect = hoveredElement.getBoundingClientRect();
      const iframeRect = iframe.getBoundingClientRect();

      return {
        left: hoveredRect.left + iframeRect.left,
        top: hoveredRect.top + iframeRect.top,
        width: hoveredRect.width,
        height: hoveredRect.height,
      };
    });

    useEffect(() => {
      const updatePosition = () => {
        const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;
        if (!iframe) return;

        const hoveredRect = hoveredElement.getBoundingClientRect();
        const iframeRect = iframe.getBoundingClientRect();

        setPosition({
          left: hoveredRect.left + iframeRect.left,
          top: hoveredRect.top + iframeRect.top,
          width: hoveredRect.width,
          height: hoveredRect.height,
        });
      };

      const canvasContainer = document.getElementById("builder-ui-canvas");

      window.addEventListener("resize", updatePosition, { passive: true });

      if (canvasContainer) {
        canvasContainer.addEventListener("scroll", updatePosition, {
          passive: true,
        });
      }

      return () => {
        window.removeEventListener("resize", updatePosition);
        if (canvasContainer) {
          canvasContainer.removeEventListener("scroll", updatePosition);
        }
      };
    }, [hoveredElement]);

    if (!position) return null;

    const hoverElement = (
      <div
        className="pointer-events-none fixed box-border size-full rounded-none border-2 border-[#506CF0]"
        style={{
          left: position.left,
          top: position.top,
          width: position.width,
          height: position.height,
        }}
      >
        <div className="pointer-events-none absolute left-[-2px] top-[-31px] rounded bg-[#506CF0] px-3 py-1 text-[0.688rem] font-semibold uppercase leading-4 tracking-wide text-white">
          {getBlockName(blockMeta)}
        </div>
      </div>
    );

    const canvasCard = document.getElementById("builder-ui-canvas-card");
    if (!canvasCard) return null;

    return createPortal(hoverElement, canvasCard);
  },
);

PreviewBlockHover.displayName = "PreviewBlockHover";
